import { Fragment } from "react";

import {
  IgCoreBackgroundColors,
  IgCoreTextColors,
} from "@intelligems/ig-design-tokens";
import { Box, SxProps } from "@mui/material";
import { IgDialog } from "@intelligems/ig-theme";

import { Z_INDEX_LEVELS } from "../constants";

const HIGHLIGHT_REGEX = new RegExp(
  [
    // Path-like tokens ending in .tsx, with optional :line(:column) suffixes,
    // e.g. "src/components/Button.tsx:12:5".
    String.raw`(?<tsxPath>\/?[\w.$@~-]+(?:\/[\w.$@~-]+)*\.tsx(?!\w)(?::\d+(?::\d+)?)?)`,
    // Component names in react-grab stack entries, e.g. "in LoginForm".
    String.raw`(?<componentName>(?<=\bin )[A-Z][\w$]*)`,
  ].join("|"),
  "g"
);

export type GrabbedContentSegment = {
  text: string;
  kind: "text" | "tsxPath" | "componentName";
};

// Splits grabbed clipboard content into plain-text, .tsx-path, and component
// name segments so the dialog can highlight the latter two. Segments
// concatenate back to the input.
export const splitGrabbedContent = (
  content: string
): GrabbedContentSegment[] => {
  const segments: GrabbedContentSegment[] = [];
  let lastIndex = 0;
  for (const match of content.matchAll(HIGHLIGHT_REGEX)) {
    if (match.index > lastIndex) {
      segments.push({
        text: content.slice(lastIndex, match.index),
        kind: "text",
      });
    }
    segments.push({
      text: match[0],
      kind: match.groups?.tsxPath !== undefined ? "tsxPath" : "componentName",
    });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    segments.push({ text: content.slice(lastIndex), kind: "text" });
  }
  return segments;
};

const DIALOG_TOKENS: Record<string, SxProps> = {
  content: {
    background: IgCoreBackgroundColors.baseLightMid,
    borderRadius: 1,
    color: IgCoreTextColors.primary,
    fontFamily: "monospace",
    fontSize: 12,
    lineHeight: 1.6,
    margin: 0,
    maxHeight: "60vh",
    overflow: "auto",
    padding: 2,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  highlight: {
    background: IgCoreBackgroundColors.accent,
    borderRadius: 0.5,
    color: IgCoreTextColors.accent,
    fontWeight: 600,
    padding: "0 3px",
  },
};

type GrabbedContentDialogProps = {
  content: string;
  onClose: () => void;
};

export const GrabbedContentDialog = ({
  content,
  onClose,
}: GrabbedContentDialogProps) => (
  <IgDialog
    content={
      <Box component="pre" sx={DIALOG_TOKENS.content}>
        {splitGrabbedContent(content).map((segment, index) =>
          segment.kind === "text" ? (
            <Fragment key={index}>{segment.text}</Fragment>
          ) : (
            <Box component="mark" key={index} sx={DIALOG_TOKENS.highlight}>
              {segment.text}
            </Box>
          )
        )}
      </Box>
    }
    handleClose={onClose}
    hideActions
    maxWidth="md"
    showCloseIcon
    showDialog
    sx={{ zIndex: Z_INDEX_LEVELS.dialog }}
    title="Copied to clipboard"
  />
);
